import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { GlobalMessageService, GlobalMessageType, OCC_USER_ID_ANONYMOUS, UserIdService } from '@spartacus/core';
import { Subject, finalize, filter, switchMap, take, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { GenAutoShipShipmentScheduleService } from '../../../services/auto-ship-shipment-schedule.service';
import { Shipment } from '../../../models/auto-ship.model';

@Component({
  selector: 'gen-auto-ship-shipment-schedule',
  templateUrl: './auto-ship-shipment-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GenAutoShipShipmentScheduleComponent implements OnDestroy {
  readonly CART_URL = '/cart';
  readonly PREVIOUS_STEP_URL = '/auto-ship/products';

  private readonly destroy$ = new Subject<void>();
  numberOfShipments: number = 0;
  shipments: Shipment[] = [];
  loading: boolean = false;

  constructor(
    private readonly router: Router,
    private readonly userIdService: UserIdService,
    private readonly cdr: ChangeDetectorRef,
    private readonly autoShipShipmentScheduleService: GenAutoShipShipmentScheduleService,
    private readonly globalMessageService: GlobalMessageService,
  ) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNumberOfShipmentsChange(count: number): void {
    this.numberOfShipments = count;
  }

  onShipmentDatesChange(shipmentDates: Shipment[]): void {
    this.shipments = shipmentDates;
  }
  
  get shipmentDatesInvalid(): boolean {
    const requiredShipments = this.shipments.slice(0, this.numberOfShipments);

    return (
      requiredShipments.length !== this.numberOfShipments ||
      requiredShipments.some(
        ({ dateControl }) => !dateControl.value || dateControl.invalid
      )
    );
  }

  private formatDate(date: Date | null): string | null {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  scheduleShipment(): void {
    if (this.loading || this.shipmentDatesInvalid) {
      return;
    }

    const shipmentDates = this.shipments.map(({ dateControl }) =>
      this.formatDate(dateControl.value)
    );

    this.loading = true;
    this.userIdService.getUserId().pipe(
      filter((userId) => !!userId && userId !== OCC_USER_ID_ANONYMOUS),
      take(1),
      switchMap((userId) =>
        this.autoShipShipmentScheduleService.scheduleShipment(userId, shipmentDates)
      ),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      })
    )
    .subscribe({
      next: () => this.navigateToCart(),
      error: (error: unknown) => this.showRequestError(error),
    });
  }

  private navigateToCart(): void {
    this.router.navigate([this.CART_URL], {
      queryParams: {
        autoShip: true,
      },
    });
  }

  private showRequestError(error: unknown): void {
    const response = error as HttpErrorResponse;
    const notifications = response.error?.generacNotifications;
    if (Array.isArray(notifications) && notifications.some(
      (notification: { message?: string }) => !!notification.message
    )) {
      // GenNotificationResponseInterceptor displays backend notifications with
      // their supplied message level, so do not replace them with the OCC error.
      return;
    }

    const message = response.error?.errors?.[0]?.message
      || response.error?.message
      || (typeof response.error === 'string' ? response.error : undefined)
      || response.message;

    this.globalMessageService.add(
      message || { key: 'autoShip.schedule.scheduleError' },
      GlobalMessageType.MSG_TYPE_ERROR,
    );
  }
}
